import { Link } from "react-router-dom";

import "./ThemeCard.css";

import type { ThemeListItem } from "@/types/themes";

import { formatRelative } from "./formatRelative";
import { StaleChip } from "./StaleChip";

export interface ThemeCardProps {
  readonly theme: ThemeListItem;
  readonly categoryName?: string;
}

const ID_SHORT_LEN = 8;

function statusClassName(status: string): string {
  const normalised = status.toUpperCase();
  if (normalised === "ACTIVE") return "theme-card__status theme-card__status--active";
  if (normalised === "ARCHIVED")
    return "theme-card__status theme-card__status--archived";
  return "theme-card__status theme-card__status--draft";
}

/**
 * CR-111 — enterprise-UX themes-grid card.
 *
 * Layout: hero thumbnail → header row (title link + status chip) → meta
 * row (category chip + monospace short-UUID) → footer row (demo tenant
 * badge + last_generated relative + stale chip when applicable).
 *
 * The card is self-contained: no data fetching, no refetch wiring.
 * The ThemesListPage feeds a single ThemeListItem + the category name
 * resolved against the shared categories cache.
 */
export function ThemeCard({ theme, categoryName }: ThemeCardProps): JSX.Element {
  const idShort = theme.id.slice(0, ID_SHORT_LEN);
  const relative = formatRelative(theme.preview_last_generated);

  return (
    <article className="theme-card" data-testid={`theme-card-${theme.slug}`}>
      <div className="theme-card__hero">
        {theme.preview_hero_url ? (
          <img
            src={theme.preview_hero_url}
            alt={`Feature graphic for ${theme.name}`}
            className="theme-card__hero-image"
            loading="lazy"
          />
        ) : (
          <div className="theme-card__hero-placeholder" aria-hidden="true">
            <span>No preview</span>
          </div>
        )}
      </div>

      <div className="theme-card__body">
        <div className="theme-card__header">
          <h3 className="theme-card__title">
            <Link
              className="theme-card__title-link"
              to={`/themes/${encodeURIComponent(theme.slug)}`}
            >
              {theme.name}
            </Link>
          </h3>
          <span className={statusClassName(theme.status)}>{theme.status}</span>
        </div>

        <div className="theme-card__meta">
          {categoryName ? (
            <span className="theme-card__chip theme-card__chip--category">
              {categoryName}
            </span>
          ) : null}
          <code className="theme-card__id" title={theme.id}>
            {idShort}
          </code>
        </div>

        <div className="theme-card__footer">
          {theme.preview_tenant_code ? (
            <span className="theme-card__chip theme-card__chip--tenant">
              {theme.preview_tenant_code}
            </span>
          ) : null}
          <span className="theme-card__timestamp" title={theme.preview_last_generated ?? undefined}>
            {relative}
          </span>
          <StaleChip active={theme.is_stale} />
        </div>
      </div>
    </article>
  );
}
